import { SplashScreen } from "@capacitor/splash-screen";
import { Capacitor } from "@capacitor/core";
import { loadBodyTheme, setStatusBarStyle } from "@/functions/theme";
import { App } from "@capacitor/app";
import { pingVersions } from "@/api-connectors/basicRequests";
import { modalController, popoverController } from "@ionic/vue";
import { route, ROUTE_NAME } from "@/router/index";
import { popAlertOk } from "@/functions/alert";
import i18n from "@/plugins/vue-i18n";
import { isIOS } from "@vueuse/core";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { useUserStore } from "@/stores/user";

const routeExceptions: string[] = [
    ROUTE_NAME.LOGIN,
    ROUTE_NAME.REGISTER_ADMIN,
    ROUTE_NAME.REGISTER_TEAM,
];

const overlayOpen = async (): Promise<boolean> => {
    return (
        !!(await modalController.getTop()) || !!(await popoverController.getTop())
    );
};

const reloadException = async (): Promise<boolean> => {
    return (
        (await overlayOpen()) ||
        (typeof route.value.name === "string" &&
            routeExceptions.includes(route.value.name))
    );
};

type Comparator = -1 | 0 | 1;
type VersionCode = string;
type VersionCodeArray = [number, number, number];

const splitVersionCode = (versionCode: VersionCode): VersionCodeArray => {
    const code = versionCode.split(".");
    if (code.length !== 3) throw Error("Invalid Versioncode");
    return [Number(code[0]), Number(code[1]), Number(code[2])];
};

const compareVersionCode = (a: VersionCode, b: VersionCode): Comparator => {
    let codeA: VersionCodeArray = [0, 0, 0];
    let codeB: VersionCodeArray = [0, 0, 0];
    try {
        codeA = splitVersionCode(a);
    } catch {
        console.warn("Invalid Versioncode Api", a);
    }
    try {
        codeB = splitVersionCode(b);
    } catch {
        console.warn("Invalid Versioncode Binary/Live Update", b);
    }

    for (let i = 0; i < 3; i++) {
        if (codeA[i] > codeB[i]) return 1;
        if (codeA[i] < codeB[i]) return -1;
    }
    return 0;
};

const getChannel = () => {
    const session = useUserStore().sessionInfo.liveUpdateChannel
    return session || 'production'
}

const performUpdateCheck = async (): Promise<void> => {
    try {
        if (Capacitor.isNativePlatform()) {
            let channel = getChannel()
            App.addListener("resume", async () => {
                channel = getChannel()
                const exceptionActive = await reloadException();
                if (localStorage.shouldReloadApp === "true" && !exceptionActive) {
                    localStorage.shouldReloadApp = false;
                    await LiveUpdate.reload();
                } else {
                    const { nextBundleId } = await LiveUpdate.sync({
                        channel
                    });
                    localStorage.shouldReloadApp = nextBundleId;
                }

                const { version: binaryVersion } = await App.getInfo();
                const pingResponse = await pingVersions();

                if (!pingResponse) return;
                if (
                    compareVersionCode(pingResponse.binary.required, binaryVersion) > 0 &&
                    !exceptionActive
                ) {
                    await popAlertOk({
                        message: i18n.global.t("messages.newBinaryVersion"),
                        handler: () => {
                            const appStoreLink = isIOS
                                ? process.env.VUE_APP_STORE_LINK_IOS
                                : process.env.VUE_APP_STORE_LINK_ANDROID;
                            window.open(appStoreLink);
                        },
                    });
                }
            });

            const { nextBundleId } = await LiveUpdate.sync({
                channel
            });
            localStorage.shouldReloadApp = nextBundleId;

            const pingResponse = await pingVersions();
            if (pingResponse) {
                const { version: binaryVersion } = await App.getInfo();
                const currentVersion = process?.env?.VUE_APP_VERSION_NAME;

                if (
                    compareVersionCode(pingResponse.binary.required, binaryVersion) > 0 ||
                    compareVersionCode(pingResponse.binary.normal, binaryVersion) > 0
                ) {
                    await SplashScreen.hide();
                    await popAlertOk({
                        message: i18n.global.t("messages.newBinaryVersion"),
                        handler: () => {
                            const appStoreLink = isIOS
                                ? process.env.VUE_APP_STORE_LINK_IOS
                                : process.env.VUE_APP_STORE_LINK_ANDROID;
                            window.open(appStoreLink);
                        },
                    });
                    //Maybe on required Modal also on app resume? Set Flag?
                }

                // if (currentVersion && compareVersionCode(pingResponse.liveUpdate.required, currentVersion) > 0) await LiveUpdates.reload();
            }

            await SplashScreen.hide();
            await setStatusBarStyle();
        }
        await loadBodyTheme();
    } catch (err) {
        if (Capacitor.isNativePlatform()) {
            await SplashScreen.hide();
            await setStatusBarStyle();
        }
        await loadBodyTheme();
    }
};

export default performUpdateCheck;
